import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { getSportLabel } from "@/lib/sport-defaults";
import { palette } from "./styles";

interface SportDistributionBarProps {
  data: Array<{ sport: string; count: number }>;
}

const styles = StyleSheet.create({
  empty: {
    fontSize: palette.type.small,
    color: palette.muted,
    fontStyle: "italic",
  },
  barRow: {
    flexDirection: "row",
    height: 16,
    width: "100%",
    overflow: "hidden",
    borderRadius: 4,
  },
  segment: {
    height: "100%",
  },
  legendList: {
    marginTop: palette.spacing.md,
    flexDirection: "column",
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 2,
  },
  legendChip: {
    width: 8,
    height: 8,
    borderRadius: 2,
    marginRight: palette.spacing.sm,
  },
  legendName: {
    flex: 1,
    fontSize: palette.type.body,
    color: palette.text,
  },
  legendValue: {
    fontSize: palette.type.small,
    color: palette.muted,
    textAlign: "right",
  },
});

interface SegmentInfo {
  sport: string;
  count: number;
  pct: number;
  color: string;
  label: string;
  widthPercent: string;
}

function buildSegments(
  data: Array<{ sport: string; count: number }>,
): SegmentInfo[] {
  if (data.length === 0) return [];
  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (total === 0) return [];

  let paletteIdx = 0;
  return data.map((d, idx) => {
    const isUnassigned = d.sport === "unassigned";
    let color: string;
    if (isUnassigned) {
      color = palette.unassignedSportColor;
    } else if (idx === 0) {
      color = palette.brand;
    } else {
      color =
        palette.sportPalette[paletteIdx++ % palette.sportPalette.length] ??
        palette.muted;
    }
    return {
      sport: d.sport,
      count: d.count,
      pct: Math.round((d.count / total) * 100),
      color,
      label: isUnassigned ? "Unassigned" : getSportLabel(d.sport),
      widthPercent: `${(d.count / total) * 100}%`,
    };
  });
}

export function SportDistributionBar({ data }: SportDistributionBarProps) {
  const segments = buildSegments(data);

  if (segments.length === 0) {
    return <Text style={styles.empty}>No lockers assigned</Text>;
  }

  return (
    <View>
      <View style={styles.barRow}>
        {segments.map((seg) => (
          <View
            key={seg.sport}
            style={[
              styles.segment,
              {
                width: seg.widthPercent,
                backgroundColor: seg.color,
              },
            ]}
          />
        ))}
      </View>
      <View style={styles.legendList}>
        {segments.map((seg) => (
          <View key={seg.sport} style={styles.legendItem}>
            <View
              style={[styles.legendChip, { backgroundColor: seg.color }]}
            />
            <Text style={styles.legendName}>{seg.label}</Text>
            <Text style={styles.legendValue}>
              {seg.count} · {seg.pct}%
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
